import {
  Permissions,
  RoleUser,
  roleUsersSchema,
  User,
  UserDetails,
  UserStakeholder,
} from "@cnx/common";
import api from "services/api";
import { convertBlobToBase64 } from "utils/blob";

class UserService {
  static fetchUsersWithRoles = async (roleId?: string): Promise<RoleUser[]> => {
    const url = roleId ? `/user/with-roles/${roleId}` : "/user/with-roles";
    const { data } = await api.get(url);
    return roleUsersSchema.parse(data);
  };

  static fetchUsers = async () => {
    const { data } = await api.get<User[]>("/user");

    return data.map(({ startDate, endDate, ...rest }) => ({
      ...rest,
      startDate: new Date(startDate),
      endDate: endDate && new Date(endDate),
    }));
  };

  static fetchUsersWithRight = async (right: Permissions): Promise<User[]> => {
    const { data } = await api.get<User[]>(`/user/with-right/${right}`);
    return data.map(({ startDate, endDate, ...rest }) => ({
      ...rest,
      startDate: new Date(startDate),
      endDate: endDate && new Date(endDate),
    }));
  };

  static fetchUserDetails = async (options: {
    userId?: string;
    email?: string;
  }) => {
    const { data } = await api.get<UserDetails>(`/user/details`, {
      params: { userId: options.userId, email: options.email },
    });

    return {
      ...data,
      startDate: new Date(data.startDate),
      endDate: data.endDate && new Date(data.endDate),
    };
  };

  static getUserAvatar = async (avatarId: string) => {
    const { data } = await api.get<Blob>(`/user/avatar/${avatarId}`, {
      responseType: "blob",
    });
    return convertBlobToBase64(data);
  };

  static findByEmail = async (email: string) => {
    return api.get<User>(`/user/${email}`);
  };

  static addUser = async (body: FormData) => {
    return api.post<{ id: string }>("/user", body, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  };

  static addUserStakeholder = async (body: {
    userId: string;
    stakeholderId: string;
  }) => {
    return api.post("/user-stakeholder", body);
  };

  static deleteUserStakeholder = async (stakeholderId: string) => {
    return api.delete(`/user-stakeholder/${stakeholderId}`);
  };

  static editUser = async (body: FormData, userId: string) => {
    return api.put(`/user/${userId}`, body, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  };

  static setUserLanguage = async (language: string) => {
    return api.put(`/user/language/${language}`);
  };

  static getUserLanguage = async (): Promise<{
    data: { language: string };
    status: number;
  }> => {
    return api.get(`/user/language`);
  };

  static deleteUser = async (userId: string) => {
    return api.delete(`/user/${userId}`);
  };

  static fetchUserStakeholders = async () => {
    return api.get<UserStakeholder[]>("/user-stakeholder");
  };
}

export default UserService;
